import {
	Archive,
	Banknote,
	BellRing,
	Briefcase,
	ClipboardList,
	Coins,
	Building2,
	Diff,
	FileUp,
	Forward,
	Ghost,
	Handshake,
	Headset,
	Inbox,
	LayoutDashboard,
	MailWarning,
	MessageSquareReply,
	Wallet,
	Package,
	PackageCheck,
	PackageSearch,
	PackageX,
	Route,
	Scale,
	ScrollText,
	Settings as SettingsIcon,
	ShieldCheck,
	Split,
	Timer,
	Undo2,
	Truck,
	Users,
	Weight,
} from 'lucide-react'
import {
	CashBagDifferences,
	CashBagReceiptTab,
	MoneyOfEveryPlace,
	PerKg,
	RegistersTab,
	ShiftsTab,
} from 'pages/cashdesk'
import { Dashboard } from 'pages/dashboard'
import { DeadLetters } from 'pages/dead-letters'
import { Driver } from 'pages/driver'
import { HandoverPage } from 'pages/handover'
import {
	HubFromDriver,
	HubPartnerReturn,
	HubShelf,
	HubSorting,
	HubTrips,
	ReceivingTab,
} from 'pages/hub'
import { HubUpload } from 'pages/hub-upload'
import { PointInventoryScan } from 'pages/inventory'
import { Locations } from 'pages/locations'
import { CashBagPage, Money } from 'pages/money'
import { Notifications } from 'pages/notifications'
import { Parcels } from 'pages/parcels'
import { PointBags } from 'pages/point-bags'
import { Partners } from 'pages/partners'
import { Policies } from 'pages/policies'
import { Reconciliation } from 'pages/reconciliation'
import { Returns } from 'pages/returns'
import { Settings } from 'pages/settings'
import { Shadow } from 'pages/shadow'
import { Staff } from 'pages/staff'
import { StorageExpiry } from 'pages/storage-expiry'
import {
	Discrepancies,
	HubShelfDecisions,
	Inventories,
	StorageDecisions,
	SupportDoublePayments,
	SupportRegisters,
	SupportReturns,
	SupportShiftDifferences,
} from 'pages/support'
import { type JSX } from 'react'
import { matchPath, type RouteObject } from 'react-router'
import { PageFrame } from 'shared/components/page-frame'
import type { TranslationKey } from 'shared/lib/i18n'
import { ROLE_BINDING, STAFF_ROLES, type Role } from 'shared/lib/roles'

/**
 * The role → sections map (spec §7, §26; ADR-0029) — the one place that decides
 * which sections each role sees. The `superadmin` (the owner, 2026-10-28) is named
 * on every row: it sees every section, even those that work at one place (a Point's
 * counter, the hub's bench, the driver's own trips) — there the server refuses its
 * acts at a place, since it is bound to none. The `admin` sees neither the hub, nor
 * the central cashbox (but its «Разницы сумок»), nor the Point's «Касса», nor the
 * Shadow, nor the dead letters (the owner, 2026-10-28).
 *
 * One row per section: its path, the roles that see it, its page. A role sees
 * exactly the rows that name it, in this order, and lands on the first of them.
 * There is no "everyone" default: a row with no role is a compile error, so a new
 * section is never handed to a role by omission (ADR-0029 — a row nobody decided
 * is "no").
 *
 * **To add a section, add one row here** — `path`, `label` (a `nav.*` key),
 * `roles`, `page`, `icon`; `subpaths` for its deeper routes; `group` to list it
 * under a heading in the sidebar. Its sidebar link gets
 * `data-testid="nav-<path>"` (the path without its leading slash). Then add the
 * section to the role's expected list in `e2e/sections.spec.ts`. A section whose
 * screens are not built yet uses `<ComingSoon />`; its ticket swaps the page in
 * that row.
 *
 * One function, one section: the hub's and the central cashbox's screens are each
 * their own row (the owner, 2026-10-27), grouped in the sidebar under «Хаб» and
 * «Центральная касса», each page under its own header (`PageFrame`, titled by its
 * sidebar label). No section shows tabs for unrelated functions.
 *
 * This is presentation only. The backend reads the live staff record and refuses
 * a role any procedure it may not reach, whatever this map shows.
 */

type Section = {
	path: string
	/** The sidebar label, a `nav.*` key. */
	label: TranslationKey
	/**
	 * The sidebar heading the link is listed under, a `nav.*` key. Grouped links keep
	 * their map order under it; ungrouped ones come first, with no heading.
	 */
	group?: TranslationKey
	/** Who sees the section. Required — there is no default. */
	roles: readonly Role[]
	page: JSX.Element
	icon: JSX.Element
	/**
	 * Deeper routes of the section, seen by the same roles, never in the sidebar; its
	 * link shows as current on them too.
	 */
	subpaths?: readonly string[]
	/** Reachable by direct URL, but never offered in the sidebar (the owner, 2026-10-28). */
	notInSidebar?: boolean
	/**
	 * Offered in the sidebar only while the operator's Point has an open inventory
	 * (ticket 54): the screen exists for that day only. Its route is there always. A
	 * role bound to no Point (the superadmin) is offered it always.
	 */
	whileInventoryOpen?: boolean
}

const EVERY_ROLE = STAFF_ROLES
const HUB: readonly Role[] = ['hub', 'superadmin']
const CASHBOX: readonly Role[] = ['cashier', 'superadmin']
const SUPPORT: readonly Role[] = ['support', 'admin', 'superadmin']

// prettier-ignore
export const SECTIONS: readonly Section[] = [
	// Release 2 (ticket 47): «Остатки» on the old summary's place; a tile leads only to a section its reader sees.
	{ path: '/dashboard',       label: 'nav.dashboard',      roles: ['superadmin', 'admin', 'operator', 'support'],             page: <Dashboard opens={opensSection} />, icon: <LayoutDashboard /> },
	// Release 2 (ticket 25): the hub's screens, each its own page under «Хаб»; `/hub` is receiving.
	{ path: '/hub',                 label: 'nav.hubReceiving',     group: 'nav.hub', roles: HUB,                   page: <PageFrame title='nav.hubReceiving'><ReceivingTab /></PageFrame>,         icon: <Inbox />, subpaths: ['/hub/receiving/:manifestId'] },
	{ path: '/hub/sorting',         label: 'nav.hubSorting',       group: 'nav.hub', roles: HUB,                   page: <PageFrame title='nav.hubSorting'><HubSorting /></PageFrame>,             icon: <Split /> },
	{ path: '/hub/trips',           label: 'nav.hubTrips',         group: 'nav.hub', roles: HUB,                   page: <PageFrame title='nav.hubTrips'><HubTrips /></PageFrame>,                 icon: <Route /> },
	{ path: '/hub/shelf',           label: 'nav.hubShelf',         group: 'nav.hub', roles: HUB,                   page: <PageFrame title='nav.hubShelf'><HubShelf /></PageFrame>,                 icon: <Archive /> },
	// Ticket 42: the return flow — from the driver, and back to the partner with a signed act.
	{ path: '/hub/from-driver',     label: 'nav.hubFromDriver',    group: 'nav.hub', roles: HUB,                   page: <PageFrame title='nav.hubFromDriver'><HubFromDriver /></PageFrame>,       icon: <Undo2 /> },
	{ path: '/hub/partner-return',  label: 'nav.hubPartnerReturn', group: 'nav.hub', roles: HUB,                   page: <PageFrame title='nav.hubPartnerReturn'><HubPartnerReturn /></PageFrame>, icon: <Forward /> },
	{ path: '/driver',          label: 'nav.driver',         roles: ['superadmin', 'driver'],                                   page: <Driver />,                          icon: <Truck /> },
	// The central cashbox's screens, each its own page under «Центральная касса» (spec §17, §26; tickets 15, 16, 17, 30, 31).
	{ path: '/cashdesk',                 label: 'nav.cashdeskShifts',         group: 'nav.cashdesk', roles: CASHBOX, page: <PageFrame title='nav.cashdeskShifts'><ShiftsTab /></PageFrame>,                 icon: <Scale /> },
	{ path: '/cashdesk/bags',            label: 'nav.cashdeskBags',           group: 'nav.cashdesk', roles: CASHBOX, page: <PageFrame title='nav.cashdeskBags'><CashBagReceiptTab /></PageFrame>,           icon: <Briefcase /> },
	{ path: '/cashdesk/money',           label: 'nav.cashdeskMoney',          group: 'nav.cashdesk', roles: CASHBOX, page: <PageFrame title='nav.cashdeskMoney'><MoneyOfEveryPlace /></PageFrame>,          icon: <Banknote /> },
	{ path: '/cashdesk/bag-differences', label: 'nav.cashdeskBagDifferences', group: 'nav.cashdesk', roles: CASHBOX, page: <PageFrame title='nav.cashdeskBagDifferences'><CashBagDifferences /></PageFrame>, icon: <Diff /> },
	{ path: '/cashdesk/kg',              label: 'nav.cashdeskKg',             group: 'nav.cashdesk', roles: CASHBOX, page: <PageFrame title='nav.cashdeskKg'><PerKg /></PageFrame>,                         icon: <Weight /> },
	{ path: '/cashdesk/registers',       label: 'nav.cashdeskRegisters',      group: 'nav.cashdesk', roles: CASHBOX, page: <PageFrame title='nav.cashdeskRegisters'><RegistersTab /></PageFrame>,           icon: <ScrollText /> },
	// Support's screens, each its own page under «Поддержка» (spec §19, §26); `/support` is the Discrepancies.
	{ path: '/support',                   label: 'nav.supportDiscrepancies',    group: 'nav.support', roles: SUPPORT, page: <PageFrame title='nav.supportDiscrepancies'><Discrepancies /></PageFrame>,             icon: <Headset /> },
	{ path: '/support/hub-shelf',         label: 'nav.hubShelf',                group: 'nav.support', roles: SUPPORT, page: <PageFrame title='nav.hubShelf'><HubShelfDecisions /></PageFrame>,                     icon: <Archive /> },
	{ path: '/support/decisions',         label: 'nav.supportDecisions',        group: 'nav.support', roles: SUPPORT, page: <PageFrame title='nav.supportDecisions'><StorageDecisions /></PageFrame>,              icon: <MessageSquareReply /> },
	{ path: '/support/double-payments',   label: 'nav.supportDoublePayments',   group: 'nav.support', roles: SUPPORT, page: <PageFrame title='nav.supportDoublePayments'><SupportDoublePayments /></PageFrame>,     icon: <Coins /> },
	{ path: '/support/returns',           label: 'nav.supportReturns',          group: 'nav.support', roles: SUPPORT, page: <SupportReturns />,                                                                     icon: <Undo2 /> },
	{ path: '/support/inventories',       label: 'nav.supportInventories',      group: 'nav.support', roles: SUPPORT, page: <PageFrame title='nav.supportInventories'><Inventories /></PageFrame>,                  icon: <ClipboardList /> },
	{ path: '/support/shift-differences', label: 'nav.supportShiftDifferences', group: 'nav.support', roles: SUPPORT, page: <PageFrame title='nav.supportShiftDifferences'><SupportShiftDifferences /></PageFrame>, icon: <Scale /> },
	{ path: '/support/registers',         label: 'nav.supportRegisters',        group: 'nav.support', roles: SUPPORT, page: <PageFrame title='nav.supportRegisters'><SupportRegisters /></PageFrame>,              icon: <ScrollText /> },
	{ path: '/partners',        label: 'nav.partners',       roles: ['superadmin', 'admin'],                                    page: <Partners />,       icon: <Handshake /> },
	{ path: '/policies',        label: 'nav.policies',       roles: ['superadmin', 'admin'],                                    page: <Policies />,       icon: <ShieldCheck /> },
	{ path: '/locations',       label: 'nav.locations',      roles: ['superadmin', 'admin'],                                    page: <Locations />,      icon: <Building2 /> },
	{ path: '/parcels',         label: 'nav.parcels',        roles: ['superadmin', 'admin', 'operator', 'cashier', 'support'],  page: <Parcels />,        icon: <PackageSearch />, subpaths: ['/parcels/:parcelId', '/parcels/containers/:containerId'] },
	// Release 2 (ticket 24): the partner's file as one Manifest to the hub. A Point never works a Manifest (ticket 34).
	{ path: '/hub-upload',      label: 'nav.hubUpload',      roles: ['superadmin', 'admin'],                                    page: <HubUpload />,      icon: <FileUp /> },
	{ path: '/reconciliation',  label: 'nav.reconciliation', roles: ['superadmin', 'admin', 'operator'],                        page: <Reconciliation />, icon: <PackageX /> },
	// Release 2 (ticket 28): the operator takes a Bag from the driver, opens it and shelves its Parcels.
	{ path: '/point-bags',      label: 'nav.pointBags',      roles: ['superadmin', 'operator'],                                 page: <PointBags />,     icon: <Package />, subpaths: ['/point-bags/:containerId'] },
	// Release 2 (ticket 42): the operator accepts a buyer's return and sees what waits for the driver.
	{ path: '/returns',         label: 'nav.returns',        roles: ['superadmin', 'operator'],                                 page: <Returns />,       icon: <Undo2 /> },
	// Release 2 (ticket 54): the operator scans the shelf for an open inventory — offered only while one is open.
	{ path: '/inventory',       label: 'nav.inventory',      roles: ['superadmin', 'operator'],                                 page: <PointInventoryScan />, icon: <ClipboardList />, whileInventoryOpen: true },
	// The Point's money under «Касса» (the owner, 2026-10-27): the Shift, and the Cash bag with the Cashbox it takes;
	// the counter last (the owner, 2026-10-28) — the operator's own (ticket 04), the bearer-code lookup serves only their Point.
	{ path: '/money',           label: 'nav.money',          group: 'nav.pointMoney', roles: ['superadmin', 'operator'],          page: <PageFrame title='nav.money'><Money /></PageFrame>,         icon: <Wallet /> },
	{ path: '/money/cash-bag',  label: 'nav.cashBag',        group: 'nav.pointMoney', roles: ['superadmin', 'operator'],          page: <PageFrame title='nav.cashBag'><CashBagPage /></PageFrame>, icon: <Briefcase /> },
	{ path: '/handover',        label: 'nav.handover',       group: 'nav.pointMoney', roles: ['superadmin', 'operator'],          page: <HandoverPage />,                                     icon: <PackageCheck /> },
	// The admin decides whom a Cash bag's difference is against (the owner, 2026-10-28): the one page of the
	// central cashbox the admin keeps, in its sidebar with no heading; the cashier and the superadmin see it under «Центральная касса».
	{ path: '/cashdesk/bag-differences', label: 'nav.cashdeskBagDifferences', roles: ['admin'], page: <PageFrame title='nav.cashdeskBagDifferences'><CashBagDifferences /></PageFrame>, icon: <Diff /> },
	{ path: '/storage-expiry',  label: 'nav.storageExpiry',  roles: ['superadmin', 'admin', 'operator'],                        page: <StorageExpiry />,  icon: <Timer /> },
	{ path: '/staff',           label: 'nav.staff',          roles: ['superadmin', 'admin'],                                    page: <Staff />,          icon: <Users /> },
	{ path: '/dead-letters',    label: 'nav.deadLetters',    roles: ['superadmin'],                                             page: <DeadLetters />,    icon: <MailWarning /> },
	// Release 2 (ticket 22): the Shadow — back's feed, the nightly reconciliation, the clean-day count.
	{ path: '/shadow',          label: 'nav.shadow',         roles: ['superadmin'],                                    page: <Shadow />,         icon: <Ghost /> },
	// Release 2 (ticket 46): staff notification groups in Telegram, the binding by code, the log.
	{ path: '/notifications',   label: 'nav.notifications',  roles: ['superadmin', 'admin'],                                    page: <Notifications />,  icon: <BellRing /> },
	{ path: '/settings',        label: 'nav.settings',       roles: EVERY_ROLE,                                   page: <Settings />,       icon: <SettingsIcon />, notInSidebar: true },
]

/** Whether `role` sees the section at `path` — what a screen asks before it links there. */
function opensSection(role: Role, path: string): boolean {
	return sectionsFor(role).some((section) => section.path === path)
}

/** The roles that work from a phone: one column, no sidebar (spec §7 — the driver). */
export const PHONE_ROLES: readonly Role[] = ['driver']

/** The sections a role sees, in map order. */
export function sectionsFor(role: Role): Section[] {
	return SECTIONS.filter((section) => section.roles.includes(role))
}

/**
 * The sidebar links a role is offered. `inventoryOpen` — whether the operator's Point
 * has an open inventory now; a section shown only then is left out otherwise.
 */
export function sidebarFor(role: Role, inventoryOpen = false): Section[] {
	return sectionsFor(role).filter(
		(section) =>
			!section.notInSidebar &&
			(!section.whileInventoryOpen || inventoryOpen || !atPoint(role)),
	)
}

/** Whether the role works at one Point — the one whose open inventory it watches. */
function atPoint(role: Role): boolean {
	return ROLE_BINDING[role] === 'point'
}

/** Whether `pathname` is the section's own page or one of its deeper routes. */
export function isCurrent(section: Section, pathname: string): boolean {
	return [section.path, ...(section.subpaths ?? [])].some((path) => matchPath(path, pathname) !== null)
}

/**
 * The sidebar's blocks, in the order drawn: the ungrouped links first, with no
 * heading, then each group under its heading, in map order.
 */
export function sidebarGroups(sections: readonly Section[]): { group?: TranslationKey; sections: Section[] }[] {
	const blocks: { group?: TranslationKey; sections: Section[] }[] = [{ sections: [] }]
	for (const section of sections) {
		const block = blocks.find((b) => b.group === section.group)
		if (block) block.sections.push(section)
		else blocks.push({ group: section.group, sections: [section] })
	}
	return blocks.filter((b) => b.sections.length > 0)
}

/**
 * Whether the sidebar asks the server about an open inventory: only a role at a
 * Point, one of whose sections is offered only while one is open.
 */
export function watchesInventory(role: Role): boolean {
	return atPoint(role) && sectionsFor(role).some((section) => section.whileInventoryOpen)
}

/** The route tree of a signed-in role: its sections and their deeper routes. */
export function routesFor(role: Role): (RouteObject & { path: string })[] {
	return sectionsFor(role).flatMap((section) => [
		{ path: section.path, element: section.page },
		...(section.subpaths ?? []).map((path) => ({ path, element: section.page })),
	])
}
